import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Svp, SvpData, SvpEntity } from '../../models/svp.model';
import { SchoolService } from '../../../schools/services/school-service/school-service';
import { RvpVersionService } from '../../../rvp-version/service/rvp-version-service/rvp-version-service';
import { HttpClient, HttpErrorResponse, httpResource } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable, tap } from 'rxjs';
import { NotificationService } from '../../../core/services/notification-service/notification-service';
import { DataConsistencyReporter } from '../../../core/services/data-consistency-reporter/data-consistency-reporter';
import { isSvpLockConflict } from '../../../core/models/problem-detail.model';
import { describeApiError } from '../../../core/errors/api-error';
import { ApiErrorHandler } from '../../../core/errors/api-error-handler';

@Injectable({
  providedIn: 'root',
})
export class SvpService {
  private readonly http = inject(HttpClient);
  private readonly notificationService = inject(NotificationService);
  private readonly apiErrors = inject(ApiErrorHandler);
  private readonly dataConsistencyReporter = inject(DataConsistencyReporter);
  schoolService = inject(SchoolService);
  private readonly rvpVersionService = inject(RvpVersionService);

  readonly #svpsResource = httpResource<SvpEntity[]>(() => {
    const schoolId = this.schoolService.selectedSchoolId();
    if (!schoolId) {
      return undefined;
    }
    return `${environment.apiUrl}/svps?schoolId=${schoolId}`;
  });
  readonly #resolvedSvps = computed<{ svps: Svp[]; dropped: string[] }>(() => {
    if (
      this.#svpsResource.status() === 'error' ||
      this.#svpsResource.isLoading() ||
      this.schoolService.isLoading() ||
      this.rvpVersionService.isLoading()
    ) {
      return { svps: [], dropped: [] };
    }
    // value() throws while the resource is in an error state, so this must come after that guard.
    const entities = this.#svpsResource.value();
    if (!entities) {
      return { svps: [], dropped: [] };
    }

    const schools = this.schoolService.schools();
    const rvpVersions = this.rvpVersionService.rvpVersions();
    // Reference data not loaded (e.g. loaded later than the SVPs, or cleared during logout) is
    // "not ready", not a real inconsistency — bail instead of dropping every SVP.
    if (schools.length === 0 || rvpVersions.length === 0) {
      return { svps: [], dropped: [] };
    }

    const dropped: string[] = [];
    const svps = entities.flatMap((entity) => {
      const school = schools.find((s) => s.id === entity.schoolId);
      if (!school) {
        dropped.push(`school ${entity.schoolId} (SVP ${entity.id})`);
        return [];
      }

      const rvpVersion = rvpVersions.find((r) => r.id === entity.rvpVersionId);
      if (!rvpVersion) {
        dropped.push(`RVP version ${entity.rvpVersionId} (SVP ${entity.id})`);
        return [];
      }

      return [{ ...entity, school, rvpVersion }];
    });

    return { svps, dropped };
  });

  readonly svps = computed<Svp[]>(() => {
    const { svps, dropped } = this.#resolvedSvps();
    return dropped.length ? [] : svps;
  });
  #mutationLoading = signal(false);
  readonly isLoading = computed(() => this.#svpsResource.isLoading() || this.#mutationLoading());

  constructor() {
    effect(() => {
      if (this.#svpsResource.status() === 'error') {
        this.apiErrors.notify(this.#svpsResource.error(), 'SvpService.loadSvps');
      }
    });

    effect(() => {
      this.dataConsistencyReporter.reportDropped(this.#resolvedSvps().dropped, 'SvpService.svps');
    });
  }

  reloadSvps(): void {
    this.#svpsResource.reload();
  }

  /**
   * Posts the new ŠVP and hands the caller the request, so a dialog that owns the action can see
   * it fail and keep the filled-in form open. The list refresh stays here; the error does not.
   */
  createSvp(data: SvpData): Observable<SvpEntity> {
    return this.http
      .post<SvpEntity>(`${environment.apiUrl}/svps`, data)
      .pipe(tap(() => this.#svpsResource.reload()));
  }

  /** @see createSvp — the caller owns the failure, the lock conflict included. */
  updateSvp(id: string, data: SvpData): Observable<SvpEntity> {
    return this.http
      .put<SvpEntity>(`${environment.apiUrl}/svps/${id}`, data)
      .pipe(tap(() => this.#svpsResource.reload()));
  }

  deleteSvp(id: string): void {
    this.#mutationLoading.set(true);
    this.http.delete(`${environment.apiUrl}/svps/${id}`).subscribe({
      next: (_) => {
        this.#mutationLoading.set(false);
        this.#svpsResource.reload();
      },
      error: (error: HttpErrorResponse) => {
        this.#mutationLoading.set(false);
        if (isSvpLockConflict(error)) {
          this.#reportLockConflict(error);
          return;
        }
        this.apiErrors.notify(error, 'SvpService.deleteSvp');
      },
    });
  }

  // The ŠVP was locked (possibly by someone else). Surface the server's message and refresh the
  // list so its lock indicators reflect reality. Not `SvpLockService.handleLockConflict`, which
  // depends on this service.
  #reportLockConflict(error: HttpErrorResponse): void {
    this.notificationService.showPersistentError(describeApiError(error).message, 'Zavřít');
    this.#svpsResource.reload();
  }
}
